import cloneDeep from 'lodash/cloneDeep';
import { provideService } from '../services/provider';

const initialState = {
  equifax: {
    equifaxIframeQsURL: '',
    showEquifaxIframe: false,
    transId: null,
  },
  payload: {
    name: null,
    address: null,
    city: null,
    state: null,
    zip: null,
    bureauCode: null,
  },
  showPinPage: false,
  usCreditScore: null,
  bureauScoreRanges: {
    A: { lowerBound: 800, upperBound: 850 },
    B: { lowerBound: 740, upperBound: 799 },
    C: { lowerBound: 670, upperBound: 739 },
    D: { lowerBound: 580, upperBound: 669 },
    E: { lowerBound: 280, upperBound: 579 },
  },
  bureauCode: null,
};

const mutations = {
  SET_SHOW_EQUIFAX_IFRAME(state, value) {
    state.equifax.showEquifaxIframe = value;
  },
  SET_EQUIFAX_IFRAME_URL(state, value) {
    state.equifax.equifaxIframeQsURL = value;
  },
  SET_EQUIFAX_TRANSID(state, value) {
    state.equifax.transId = value;
  },
  SET_SHOW_PIN_PAGE(state, value) {
    state.showPinPage = !!value;
  },
  SET_BUREAU_SCORE_RANGES(state, value) {
    state.bureauScoreRanges = value;
  },
  SET_CREDIT_SCORE(state, value) {
    state.usCreditScore = value;
  },
  SET_BUREAU_CODE(state, value) {
    state.bureauCode = value;
  },
};
const actions = {
  createUserPayload({ rootState }, addOns) {
    const { user, business } = rootState;
    const basePayload = {
      name: `${user.firstName} ${user.lastName}`,
      address: user.streetAddress,
      city: user.city,
      state: user.state,
      zip: user.zipCode,
      bureauCode: business.info.creditBureauPref,
    };
    if (addOns && Object.keys(addOns).length !== 0 && addOns.constructor === Object) {
      return Object.freeze(Object.assign({}, basePayload, addOns));
    }
    return Object.freeze(basePayload);
  },
  /**
   * Manages dispatching subflows and routing return values for 700credit
   * @param {*} param0 - context object
   * @return This is a one-way street, buddy! No going back now.
   */
  async manage700Flow({ rootState, dispatch }) {
    const { business } = rootState;
    const payload = await dispatch('createUserPayload');
    let report;

    const isDemo = process.env.VUE_APP_APP_ENV === 'test' || rootState.business.info.isDemo;
    const creditService = provideService({
      product: 'credit',
      isDemo,
    });

    if (isDemo) {
      report = await creditService.prequalify700();
      dispatch('reportHandler', report);
      return;
    }

    if (business.info.creditBureauPref === 'EFX') {
      report = await dispatch('equifaxSubflow', payload);

      // res is null if iFrame flow taken
    } else {
      report = await dispatch('getReport', payload);
    }

    if (report) {
      // dispatch always returns a promise, add the await keyword for a race-condition just in case.
      await dispatch('reportHandler', report);
    }
  },
  /**
   * Act on Equifax iFrame's poseMessage control events
   * @param {context} param0 - context object
   * @param {event} event - incoming Equifax event from window postMessage handler
   *                        to be dealt with as messages or success/failures
   */
  async manageEquifaxFlow({ dispatch }, event) {
    const filteredMessage = await dispatch('equifaxMessageFilter', event);
    // execute function specified by filteredMessage.type
    ({
      // eslint-disable-next-line no-console
      message: msg => console.log(msg.value),
      success: async msg => {
        if (msg.value) {
          dispatch('reportHandler', await dispatch('getReportEquifax'));
        }
      },
    })[filteredMessage.type](filteredMessage);
  },
  /**
   * Separate iframe events into success, failure, and message buckets for Equifax flow manager.
   * Also, close iframe for any permanent failures.
   * @param {context} param0 - Context object
   * @param {*} event - Event from Equifax iframe
   */
  equifaxMessageFilter({ commit }, event) {
    const eventStatus = JSON.parse(event.data).Status;
    let result;

    switch (eventStatus) {
      case 'OOW_PRESENTED':
        result = { type: 'message', value: 'iFrame displayed.' };
        break;
      case 'OOW_INCORRECT':
        result = { type: 'message', value: 'Round 1 failed' };
        break;
      case 'RESULT_SUCCESS':
        result = { type: 'success', value: true };
        break;
      case 'RESULT_FAIL':
      case 'RESULT_PROCESS_COMPLETE':
      case 'OOW_NOT_ANSWERED':
      default:
        commit('SET_SHOW_EQUIFAX_IFRAME', false);
        return { type: 'success', value: false };
      // TODO: Do something here
    }
    return result;
  },
  /**
   * Handles credit report results, failures, and pin requests
   * @param {*} param0 - context object
   * @param {*} report - credit report response
   */
  reportHandler({ commit }, report) {
    switch (report.data.resultCode) {
      case 'A': // Score found and returned
        commit('SET_CREDIT_SCORE', report.data.consumerScore.tier);
        commit('SET_BUREAU_SCORE_RANGES', report.data.bureauScoreRanges);
        commit('SET_BUREAU_CODE', report.data.bureauCode);
        break;
      case 'Z': // Security Freeze: Show pin page
        commit('SET_SHOW_PIN_PAGE', true);
        break;
      case 'F': // Consumer score is below the minimum cutoff range
      case 'E': // Error or consumer is a minor
      case 'X': // Consumer could not be found
      case 'PC': // Score not found
      case 'NR': // No response from credit bureau
      case 'R': // Information or inquiry is reported as FRAUD
      case 'S': // SSN requested and denied
      case 'T': // Thin file
      case 'W': // Waiting for q and a submission process
      default:
        // Show results page without data
        commit('SET_BUREAU_SCORE_RANGES', report.data.bureauScoreRanges);
    }
  },
  /**
   * Submits payload + pin for locked credit account
   * @param {*} param0 - Context object
   * @param {*} pin - Alphanumeric pin
   */
  async submitAccessPin({ dispatch, rootState }, pin) {
    const payloadWithPin = dispatch('createUserPayload', { accessPin: pin });
    try {
      const isDemo = process.env.VUE_APP_APP_ENV === 'test' || rootState.business.info.isDemo;
      const creditService = provideService({
        product: 'credit',
        isDemo,
      });

      const res = await creditService.prequalify700WithPin(payloadWithPin);
      dispatch('reportHandler', res);
    } catch (err) {
      this._vm.$log('Error sending pin for US credit: ', err);
    }
  },
  /**
   * Checks if Equifax identification required.
   * Either get report or dispatch iFrame for questions.
   * Flow picked back up in PostMessageHandler
   * @param {context} param0 - Context object
   * @param {*} payload - User data to submit
   * @returns {null|EquifaxReport} null if iFrame dispatched, or Equifax report
   */
  async equifaxSubflow({ commit, rootState }, payload) {
    const isDemo = process.env.VUE_APP_APP_ENV === 'test' || rootState.business.info.isDemo;
    const creditService = provideService({
      product: 'credit',
      isDemo,
    });
    const res = await creditService.prequalify700Equifax(payload);
    const { resultCode, equifaxIframeQsURL, transId } = res.data;
    if (resultCode === 'EQ') {
      commit('SET_EQUIFAX_IFRAME_URL', equifaxIframeQsURL);
      commit('SET_EQUIFAX_TRANSID', transId);
      commit('SET_SHOW_EQUIFAX_IFRAME', true);
    } else {
      return res;
    }
    return null;
  },
  /**
   * Submits payload to equifax after successful identity validation in iFrame.
   *
   * @param {*} context
   * @param {*} payload
   */
  async getReportEquifax({ state, dispatch, rootState }) {
    const isDemo = process.env.VUE_APP_APP_ENV === 'test' || rootState.business.info.isDemo;
    const creditService = provideService({
      product: 'credit',
      isDemo,
    });
    const res = await creditService.prequalify700EquifaxAnswered(
      await dispatch('createUserPayload', { transId: state.equifax.transId })
    );
    return res;
  },
  async getReport({ rootState }, payload) {
    const isDemo = process.env.VUE_APP_APP_ENV === 'test' || rootState.business.info.isDemo;
    const creditService = provideService({
      product: 'credit',
      isDemo,
    });
    const res = await creditService.prequalify700(payload);
    return res;
  },
};
const getters = {
  getEquifaxIframeQsURL: state => state.equifax.equifaxIframeQsURL,
  getShouldShowEquifaxIframe: state => state.equifax.showEquifaxIframe,
  getShowPinPage: state => state.showPinPage,
  getBureauScoreRanges: state => state.bureauScoreRanges,
  getUsCreditScore: state => state.usCreditScore,
};

export default {
  namespaced: true,
  state: cloneDeep(initialState),
  mutations,
  actions,
  getters,
};
